import _ from 'underscore';

import {assertAndContinue} from 'Asserts';
import * as vo from 'components/VisibilityOptions';
import * as MapsConstants from 'constants/MapsConstants';
import {LayeringMode, SliceComponentName} from 'constants/MapsConstants';
import {Strand} from 'constants/MolbioConstants';
import PolymerAnnotationsView, {PolymerAnnotationsViewModel} from 'molbio/maps/linear/PolymerAnnotationsView';
import Slice from 'molbio/maps/linear/Slice';
import {SliceView} from 'molbio/maps/linear/SliceView';
import * as mapsutil from 'molbio/maps/MapsUtil';
import * as selectables from 'molbio/maps/selectables/Selectables';
import Type from 'molbio/maps/Type';
import {ProteinAnnotationVersion} from 'molbio/proteins/util/ProteinConstants';
import * as sequtil from 'molbio/SeqUtil';
import {TabState} from 'Navigation';

import Protein from './Protein';
import ProteinActions from './ProteinActions';
import ProteinStore from './ProteinStore';

export interface ProteinAnnotationViewAttributes extends selectables.SelectableAttributes {
  name: string;
  color: string;
  start: number;
  end: number;
  hover: boolean;
  selected: boolean;
  proteinId: string | null;
}

/**
 * Note: this is not a view, it's a model.
 */
export class ProteinAnnotationView extends selectables.RegionSelectable<Protein> {
  setting!: string;

  defaults(): Partial<ProteinAnnotationViewAttributes> {
    return {
      // @ts-expect-error
      name: null,
      // @ts-expect-error
      color: null,
      hover: false,
      selected: false,
      proteinId: null,
    };
  }

  // We synchronize annotation hover state to the store;
  // `hover` is then injected into these ProteinAnnotation backbone when
  // ProteinAnnotationsView.getData is called
  _syncHoverStateToStore(_annotation: unknown, isHovering: boolean): void {
    ProteinActions.hoverAnnotation(this.proteinId(), isHovering ? this.id : null);
  }

  _syncSelectedStateToStore(_annotation: unknown, isSelected: boolean): void {
    ProteinActions.selectAnnotation(this.proteinId(), isSelected ? this.id : null);
  }

  initialize(attributes: ProteinAnnotationViewAttributes, options: {syncToStore: boolean}): void {
    super.initialize(attributes, options);
    this.setting = vo.ANNOTATIONS.name;
    if (options.syncToStore) {
      this.on('change:hover', this._syncHoverStateToStore.bind(this));
      this.on('change:selected', this._syncSelectedStateToStore.bind(this));
    }
  }

  name(): ProteinAnnotationViewAttributes['name'] {
    return this.get('name');
  }
  color(): ProteinAnnotationViewAttributes['color'] {
    return this.get('color');
  }
  start(): ProteinAnnotationViewAttributes['start'] {
    return this.get('start');
  }
  end(): ProteinAnnotationViewAttributes['end'] {
    return this.get('end');
  }
  strand(): Strand {
    return 1;
  }
  layerStart(): ProteinAnnotationViewAttributes['start'] {
    return this.start();
  }
  layerEnd(): ProteinAnnotationViewAttributes['end'] {
    return this.end();
  }
  uid(): string {
    return this.cid;
  }
  hover(): ProteinAnnotationViewAttributes['hover'] {
    return this.get('hover');
  }
  selected(): ProteinAnnotationViewAttributes['selected'] {
    return this.get('selected');
  }
  proteinId(): ProteinAnnotationViewAttributes['proteinId'] {
    return this.get('proteinId');
  }
  click(protein: Protein): void {
    protein.selection().setEndpoints(this.start(), this.end());
    this.set('selected', true);
  }
  dblClick(protein: Protein): void {
    // TODO(T6006): Double-click should open the annotations panel.
  }
}

export interface ProteinAnnotationsViewOptions {
  model: PolymerAnnotationsViewModel<Protein>;
  tabstate: TabState;
  visibilityOptions: vo.VisibilityOptions;
  slice: Slice;
  parent: SliceView;
  mapType: string;
  name: SliceComponentName;
}

class ProteinAnnotationsView extends PolymerAnnotationsView<
  Protein,
  PolymerAnnotationsViewModel<Protein>,
  ProteinAnnotationView
> {
  constructor(options: ProteinAnnotationsViewOptions) {
    super(options);
    this.setting = vo.ANNOTATIONS.name;
    this.className = 'proteinAnnotations';
  }

  layeringMode(): LayeringMode {
    // TODO: Temporarily overridden to restrict only to the one supported layeringMode.
    return MapsConstants.LINEAR_LAYER_FULL;
  }

  shouldSyncToStore(): boolean {
    return true;
  }

  isAnnotationHovered(annotation: ProteinAnnotationVersion): boolean {
    return ProteinStore.getHoveredAnnotationId(this.getProteinId()) === annotation.id;
  }

  isAnnotationSelected(annotation: ProteinAnnotationVersion): boolean {
    return ProteinStore.getSelectedAnnotationId(this.getProteinId()) === annotation.id;
  }

  getProteinId(): string {
    return this.model.protein().id;
  }

  getAnnotations(): Array<ProteinAnnotationVersion> {
    return this.model.protein().annotations();
  }

  getResiduesType(): Type {
    return this.model.get('layout').AminoAcids.get('type');
  }

  getData(sliceStart: number, sliceEnd: number): Array<ProteinAnnotationView> {
    // Convert POJO annotations (backed by ProteinStore) into Backbone models, and only select ones
    // that are in range for this slice
    const proteinId = this.getProteinId();
    const proteinAnnotations = this.getAnnotations()
      .map((annotation) => {
        // Inject the hover/selected state into the ProteinAnnotation Backbone model
        const hover = this.isAnnotationHovered(annotation);
        const selected = this.isAnnotationSelected(annotation);
        assertAndContinue(
          annotation.start != null && annotation.end != null,
          'Protein annotation must have start and end'
        );
        return new ProteinAnnotationView(
          {
            proteinId,
            hover,
            selected,
            ...annotation,
          },
          {
            syncToStore: this.shouldSyncToStore(),
          }
        );
      })
      .filter((annotation) =>
        sequtil.isRangeOverlap(sliceStart, sliceEnd, annotation.start(), annotation.end())
      );

    // HACK - this generates the layer orders for the annotations. Ideally we would run this once
    // per all annotations rather than once per SliceView
    const modelsAndLayers = mapsutil.getModelLayers(proteinAnnotations, {top: false});
    for (const {model, layer} of modelsAndLayers) {
      model.set(this.layeringMode(), layer, {silent: true});
    }

    return proteinAnnotations;
  }

  _updateAfterProteinChange(): void {
    this.update();
    // Since update recreates the infinite scroller, we should only set the scroll position
    // afterward.
    const proteinId = this.model.protein().id;
    const pendingJump = ProteinStore.getPendingJump(proteinId);
    if (pendingJump) {
      const {start, end} = pendingJump;
      this.model.protein().trigger('jump', start, end);
      _.defer(() => {
        ProteinActions.pendingJumpCompleted(proteinId);
      });
    }
  }

  _registerAnnotationListeners(): void {
    if (!this.shouldSyncToStore()) {
      return;
    }
    ProteinStore.addChangeListener(this._updateAfterProteinChange.bind(this));
  }
}

export default ProteinAnnotationsView;
